import { randomPuzzleId, getPuzzleAsset, renderPuzzleImage, loadPuzzleImage, PUZZLE_ASSETS } from './puzzleAssets'

export { PUZZLE_ASSETS, randomPuzzleId, getPuzzleAsset, renderPuzzleImage, loadPuzzleImage }

/** Grid size by difficulty */
export const PUZZLE_GRID = {
  easy: 3,
  hard: 4,
  brutal: 5,
}

/** Solved board: [0,1,2,...,n*n-2, null] where null is the empty slot */
export function solvedBoard(n) {
  const cells = Array.from({ length: n * n - 1 }, (_, i) => i)
  cells.push(null)
  return cells
}

export function findEmpty(board) {
  return board.indexOf(null)
}

export function neighborsOf(index, n) {
  const r = Math.floor(index / n)
  const c = index % n
  const out = []
  if (r > 0) out.push(index - n)
  if (r < n - 1) out.push(index + n)
  if (c > 0) out.push(index - 1)
  if (c < n - 1) out.push(index + 1)
  return out
}

export function canSlide(board, tileIndex, n) {
  const empty = findEmpty(board)
  return neighborsOf(empty, n).includes(tileIndex)
}

/** Swap tile with empty; returns new board or null if illegal */
export function slide(board, tileIndex, n) {
  if (!canSlide(board, tileIndex, n)) return null
  const empty = findEmpty(board)
  const next = board.slice()
  next[empty] = next[tileIndex]
  next[tileIndex] = null
  return next
}

export function isSolved(board) {
  const n = Math.sqrt(board.length)
  for (let i = 0; i < n * n - 1; i++) {
    if (board[i] !== i) return false
  }
  return board[n * n - 1] === null
}

export function shuffleStepsFor(n, difficulty = 'easy') {
  if (n === 3) return difficulty === 'easy' ? 10 : difficulty === 'hard' ? 28 : 40
  if (n === 4) return difficulty === 'easy' ? 24 : difficulty === 'hard' ? 40 : 52
  return difficulty === 'easy' ? 36 : difficulty === 'hard' ? 52 : 70
}

/**
 * Fisher-Yates via legal slides so the puzzle is always solvable.
 * Returns { board, shuffleMoves }.
 */
export function shuffleBoard(n, steps = n * n * 12) {
  const { board, shuffleMoves } = planShuffle(n, steps)
  return { board, shuffleMoves }
}

/**
 * Build a full scramble path from solved → shuffled.
 * Returns { frames, board, shuffleMoves } where frames[0] is solved.
 */
export function planShuffle(n, steps = n * n * 12) {
  let board = solvedBoard(n)
  const frames = [board.slice()]
  let lastEmpty = -1
  let moves = 0
  let guard = 0
  while (moves < steps && guard < steps * 8) {
    guard += 1
    const empty = findEmpty(board)
    const opts = neighborsOf(empty, n).filter((i) => i !== lastEmpty)
    if (!opts.length) continue
    const pick = opts[Math.floor(Math.random() * opts.length)]
    lastEmpty = empty
    board = slide(board, pick, n)
    frames.push(board.slice())
    moves += 1
  }
  if (isSolved(board)) return planShuffle(n, steps + n)
  return { frames, board, shuffleMoves: moves }
}

/** Score from move count vs a soft target based on grid + shuffle depth */
export function scorePuzzle(moves, n, shuffleMoves) {
  const target = Math.max(shuffleMoves * 0.55, n * n * 4)
  const over = Math.max(0, moves - target)
  const t = Math.min(1, over / (target * 1.8))
  return Math.max(0, Math.min(100, 100 * Math.pow(1 - t, 1.25)))
}
